import {ActionIcon, createStyles, Tooltip} from '@mantine/core';
import React, {useCallback} from 'react';

import {fetchSignedFileUrl, getChatFileDownloadUrl} from 'llm/chat-library/ChatDAO';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import copyToClipboard from 'util/clipboard/copyToClipboard';

import {FileViewerBody} from './FileViewerBody';

export interface FileViewerContentData {
  chatSessionId: string;
  externalFileId: string;
  filename: string;
  mimeType: string;
  label: string;
}

const TEXT_MIME_TYPES = new Set([
  'text/plain',
  'application/json',
  'text/markdown',
  'text/csv',
  'text/tsv',
  'text/x-python-script',
]);

function isTextMimeType(mimeType: string): boolean {
  return TEXT_MIME_TYPES.has(mimeType);
}

const useStyles = createStyles((theme) => ({
  actionBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing.xs,
    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
    flexShrink: 0,
  },
  body: {
    flex: 1,
    overflow: 'auto',
    minHeight: 0,
  },
}));

export function FileViewerContent({content}: {content: FileViewerContentData}): JSX.Element {
  const {classes} = useStyles();

  const handleDownload = useCallback(() => {
    window.open(
      getChatFileDownloadUrl(content.chatSessionId, content.externalFileId),
      '_blank',
      'noopener,noreferrer'
    );
  }, [content]);

  const handleCopy = useCallback(async () => {
    const signedUrl = await fetchSignedFileUrl(content.chatSessionId, content.externalFileId);
    const response = await fetch(signedUrl);
    const text = await response.text();
    await copyToClipboard(text);
  }, [content]);

  const showCopyButton = isTextMimeType(content.mimeType);

  return (
    <>
      <div className={classes.actionBar}>
        <Tooltip label="Download">
          <ActionIcon
            aria-label="Download file"
            variant="outline"
            size="sm"
            color="gray"
            onClick={handleDownload}
          >
            <BenchlingIcon icon="download" />
          </ActionIcon>
        </Tooltip>
        {showCopyButton && (
          <Tooltip label="Copy">
            <ActionIcon
              aria-label="Copy to clipboard"
              variant="outline"
              size="sm"
              color="gray"
              onClick={handleCopy}
            >
              <BenchlingIcon icon="copy" />
            </ActionIcon>
          </Tooltip>
        )}
      </div>
      <div className={classes.body}>
        <FileViewerBody
          chatSessionId={content.chatSessionId}
          externalFileId={content.externalFileId}
          filename={content.filename}
          mimeType={content.mimeType}
        />
      </div>
    </>
  );
}
